import {DateTime} from 'luxon';
import { sumBy, get, truncate, isEmpty, values } from 'lodash';
import styled from '@emotion/styled';
import axios from '../axios';

import { Tooltip, useTheme } from '@mui/material';

import BooleanChip from './utilities/BooleanChip';

import MultiStatusChip from './utilities/MultiStatusChip';

import {HebrewCalendar, HDate, HebrewDateEvent, GeoLocation, Location, ParshaEvent, CandleLightingEvent, HavdalahEvent, getHolidaysOnDate, flags, Zmanim} from '@hebcal/core';


const TooltipText = styled.span`
  font-size: 1.2rem;
`;

const Balance = ({ val, amount }) => {
  const theme = useTheme();
  return (
    <span style={{
      color: amount > 0 ? theme.palette.error.main : 'inherit',
      fontWeight: 500
    }}>{val}</span>
  )
}

const capitalize = (s) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const stringToColor = (string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/

/*****  Date Format Functions *****/
const ivd = (date) => { // local version of isValidDate
  date = new Date(date);
  return date && Object.prototype.toString.call(date) === "[object Date]" && !isNaN(date);
}

const dateStart = (date) => {
  return ivd(date) ? new Date(DateTime.fromJSDate(new Date(date)).startOf('day')).toISOString() : date
}

const dateEnd = (date) => {
  return ivd(date) ? new Date(DateTime.fromJSDate(new Date(date)).endOf('day')).toISOString() : date
}

const formatTime = (time, short = false) => {
  let date = DateTime.fromJSDate(new Date(time)).toFormat('h:mma')
  date = short ? date.toLowerCase().substring(0, date.length - 1) : date
  return date;
}

const timeDiff = (start, end) => {
  let startDate = DateTime.fromJSDate(new Date(start))
  let endDate = DateTime.fromJSDate(new Date(end))
  return endDate.diff(startDate, ['hours', 'minutes']).toObject()
}

const getLuxonDate = (date) => {
  return date ? DateTime.fromJSDate(new Date(date)) : null;
}

const getCCFee = (amount, rate) => Math.round(amount * (rate || 0) / 100 * 100) / 100;

const IsoDate = (date) => {
  return date.toISOString()
}

// Format date from generic format: iso or js date
const formatGenericDate = (date, mode = "default") => {
  if (!date) return "";
  let newDate = DateTime.fromISO(date)
  if (!newDate.isValid) {
    newDate = DateTime.fromJSDate(date)
  }
  let result;
  switch (mode) {
    case "short":
      result = newDate.toLocaleString(DateTime.DATE_SHORT);
      break;
    case "long":
      result = newDate.toLocaleString(DateTime.DATETIME_MED);
      break;
    default:
      result = newDate.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
      break;
  }
  return result;
};

const formatGenericTime = (time) => {
  if (!time) return "";
  let date = DateTime.fromJSDate(new Date(time)).toFormat('h:mm a')
  return date;
}

const formatGenericDateTime = (timestamp, short = false) => {
  if (!timestamp) return "";
  let date = DateTime.fromJSDate(new Date(timestamp))
  date = short ? date.toLocaleString(DateTime.DATETIME_SHORT) : date.toFormat('ccc, LLL dd, h:mm a')
  return date;
}

const formatDayDate = (timestamp) => {
  if (!timestamp) return "";
  let date = DateTime.fromJSDate(new Date(timestamp))
  date = date.toFormat("ccc, LLL d")
  return date;
}

const addMinutes = (date, minutes) => {
  let newDate = DateTime.fromJSDate(new Date(date)).plus({minutes: minutes})
  return newDate.toJSDate()
};

const relativeTime = (date) => {
  date = date ? new Date(date) : new Date();
  return DateTime.fromJSDate(date).toRelative()
};

/**
 * Formats a duration in seconds to an MM:SS string.
 * @param {number} totalSeconds - The total duration in seconds.
 * @returns {string} Formatted string (e.g., "05:03" or "125:45")
 */
const formatDurationFromSeconds = (totalSeconds) => {
  // Handle null, undefined, or negative values
  if (!totalSeconds || totalSeconds < 0) return "00:00";

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Pad seconds with a leading zero if they are less than 10
  const paddedSeconds = seconds.toString().padStart(2, '0');
  
  // If you want minutes to also be zero-padded (e.g., 05:03), 
  // use minutes.toString().padStart(2, '0')
  return `${minutes.toString().padStart(2, '0')}:${paddedSeconds}`;
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
/**** Currency Format Functions ****/
const formatCurrency = (number) => typeof +number === 'number' ? currencyFormatter.format(number) : `$0.00`

const fxn = (number) => typeof +number === 'number' ? +number.toFixed(2) : 0

/* Phone Format */
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return '';
  }
  // Remove all non-digit characters
  let digits = phoneNumber.toString().replace(/\D/g, '');

  if (digits.startsWith('1')) {
    digits = digits.substring(1);
  }

  // console.log(digits, "digits");
  
  // Check if we have exactly 10 digits
  if (digits.length !== 10) {
    return phoneNumber; // Return original if not 10 digits
  }
  
  // Format as (xxx) xxx-xxxx
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/* Table filter functions */

const getQueries = (query) => {
  let queryString = "";
  // iterate through query object
  for (let key in query) {
    // if query value is not an object (primitive), append to queryString
    if (typeof query[key] !== "object") {
      queryString += `&${key}=${query[key]}`;
    } else if (query[key] instanceof Date) {
      queryString += `&${key}=${query[key].toISOString()}`; // Convert date to ISO string
    } else if (Array.isArray(query[key])) { // if query value is an array, join values and append to queryString
      queryString += `&${key}=${query[key].join(",")}`;
    } else { // if query value is an object, iterate through object and append to queryString
      for (let subKey in query[key]) {
        let value = Array.isArray(query[key][subKey]) ? query[key][subKey].join(",") : query[key][subKey]; // if value is an array, join values
        queryString += `&${key}[${subKey}]=${value}`;
      }
    }
  }
  return queryString;
}

/*****  Table display functions *****/

const displayTruncated = (string, len) => {
  if (isEmpty(len)) {
    len = {length: 10}
  }
  let newString = truncate(string, {...len})
  return newString && newString !== "-" ? <Tooltip title={<TooltipText>{string}</TooltipText>} arrow><span>{newString}</span></Tooltip> : newString;
}
const formatDisplay = (obj1, obj2, isMobile) => {
  const { render } = obj1
    
  let string = ''
  let val = render ? render(obj2) : get(obj2, obj1.id)

  
  if (obj1.boolean) { // for booleans
     return <BooleanChip check={val} values={obj1.values} />
  }

  if (obj1.multi) { // for multiStatus
    return <MultiStatusChip value={val} values={obj1.values} />
  }

  val = val || val === 0 ? val : '-';

  if (obj1.currency && obj2.rateType !== 'percentage') {
    string = obj1.noZero && val == 0 ? "-" : formatCurrency(val);
  }
  else if (obj1.currency && obj2.rateType === 'percentage') string = `%${val}`
  else if (obj1.balance) {
    string = obj1.noZero && val == 0 ? "-" : formatCurrency(val);
    return <Balance val={string} amount={val}></Balance>
  }
  else if (obj1.date) {
    string = formatGenericDate(val, isMobile ? "short" : "false")
    string = "Invalid DateTime" === string ? "-" : string
  } 
  else if (obj1.dateShort) {
    string = formatGenericDate(val, "short")
    string = "Invalid DateTime" === string ? "-" : string
  } 
  else if (obj1.dateTime) {
    string = formatGenericDateTime(val, "short")
    string = "Invalid DateTime" === string ? "-" : string
  } 
  else if (obj1.phone) string = formatPhoneNumber(val)
  else if (obj1.array) {
    let str = Array.isArray(val) ? val : JSON.parse(val);
    string = str.join(", ");
  }
  else string = val

  if (!render && obj1.capitalize) {
    string = capitalize(string)
  }

  if (!render && obj1.truncate) {
    let len = typeof obj1.truncate === 'number' ? {length: obj1.truncate} : {};
    string = displayTruncated(string, {...len})
    return string;
  }

  if (isMobile) {
    if (!render) {
      string = displayTruncated(string, {length: 15})
    }
  } else {
    if (!render) {
      string = displayTruncated(string, {length: 20})
    }
  }
  
  return string || string === 0 ? string : '-';
}

const getViewableRows = (rows, page, rowsPerPage, operation = "server") => {
    let visibleRows = `0`;
    if (rows) {
      let begin = +rowsPerPage * +page + 1
      let visible = operation === "server" ? rows.length : rows.length - +page * +rowsPerPage;
      let end = begin + Math.min(rowsPerPage, visible) - 1;
      visibleRows = `${begin}-${end}`;
    }
    return visibleRows;
  }

const formatLength = (val, unit) => {
  if (!val) return "";
  val = val.toString().trim().match(/\d/g).join("");
  return unit === "cm" ? `${val}cm` : `${val}″`;
}


/* boolean functions */

const strToBool = (val) => {
  let isTrueSet = (String(val)?.toLowerCase() === 'true');
  return isTrueSet;
}

const boolToStr = (val) => {
  if (val === null || val === undefined) return null;
  return val ? 'true' : 'false';
}

const equalIfNotNull = (val1, val2) => {
  return val1 === val2 || (!val1 && !val2);
}

const isValidEmail = (email) => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);

const detectEnter = (e, callback) => {
  if (e.keyCode === 13) {
    return callback();
  }
  return false;
};

const parseTime = (time) => {
  time = new Date(time);
  if (!ivd(time)) return 0;
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  return hours + minutes / 60 + seconds / 3600;
};


/* Hebrew event/date functions */

const getHebrewDate = (date) => {
  if (!ivd(date)) return "";
  let hDate = new HDate(date);
  let hebDate = new HebrewDateEvent(hDate)
  return hebDate.renderBrief('he-x-NoNikud');
}

const getEvents = (options, start, end) => {
  let calOptions = {
    ...options,
    start,
    end
  }
  const events = HebrewCalendar.calendar({
    ...calOptions
  });

  return events;
}

const getParsha = (events) => {
  let parsha = events.filter(ev => ev instanceof ParshaEvent)[0] || null;
  if (parsha) {
    parsha = parsha.render('he-x-NoNikud');
  }
  return parsha;
}

const getCandleLighting = (events, full = false) => {
  let candleLighting = events.filter(ev => ev instanceof CandleLightingEvent)[0] || null;
  if (candleLighting && !full) {
    candleLighting = candleLighting.eventTime;
  }
  return candleLighting;
}

const getHavdala = (events, full = false) => {
 let havdala = events.filter(ev => ev instanceof HavdalahEvent)[0] || null;
  if (havdala && !full) {
    havdala = havdala.eventTime;
  }
  return havdala;
}

const getDayBefore = (date) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - 1);
  return newDate;
}

const getDayAfter = (date) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
}

const getHolidayEvents = (date) => {
  let events = getHolidaysOnDate(date);
  if (!events?.length) return [];
  const excludeMask = flags.MODERN_HOLIDAY | flags.MINOR_FAST | flags.IL_ONLY;
  const filteredEvents = events.filter(ev => 
    !(ev.getFlags() & excludeMask)
  );
  let holidayEvents = filteredEvents.map(ev => {
    let isChag = !(ev.getFlags() & flags.ROSH_CHODESH);
    let isRC = ev.getFlags() & flags.ROSH_CHODESH;
    return {
      title: ev.render('he-x-NoNikud'),
      className: isRC ? "roshchodesh" : isChag ? "major-holiday" : "holiday",
      mask: ev.mask,
      desc: ev.desc
    }
  })
  return holidayEvents;
}

const isMajorHoliday = (date) => {
  let events = getHolidaysOnDate(date);
  if (!events?.length) return null;
  const includeMask = flags.CHAG;
  const filteredEvents = events.filter(ev => 
    (ev.getFlags() & includeMask)
  );
  return filteredEvents.length;
}

const flagEvent = (date, flag) => {
  let events = getHolidaysOnDate(date);
  if (!events?.length) return null;
  const filteredEvents = events.filter(ev => 
    (ev.getFlags() & flag)
  );
  return filteredEvents.length;
}

const checkBookingAvailabilityByDate = (date, setting = {}) => {
  let availability = {}
  let day = date.getDay();
  if (day === 6) {
    let sa = setting.shabbos_booking;
    availability = {
      [`$${sa}`]: true
    }
  } else if (isMajorHoliday(date)) {
    let yot = setting.yom_tov_booking;
    availability = {
      [`$${yot}`]: true
    }
  } else if (flagEvent(date, flags.CHOL_HAMOED)) {
    let ch = setting.chol_hamoed_booking;
    availability = {
      [`$${ch}`]: true
    }
  } else if (flagEvent(date, flags.ROSH_CHODESH)) {
    let rc = setting.rosh_chodesh_booking;
    availability = {
      [`$${rc}`]: true
    }
  } else {
    availability = {
      $enabled: true
    }
  }
  return availability;
}

const checkBookingAvailabilityByTime = (date, time, setting = {}) => {
  // get sunset time
  let data = setting?.data
  let location = locations?.[data?.location]
  if (!location) {
    location = locations["Monroe"];
  }
  location = new GeoLocation(null, location.lat, location.lng, 0, location.tz);
  let today = checkBookingAvailabilityByDate(date, data);
  let tomorrow = checkBookingAvailabilityByDate(getDayAfter(date), data);
  let zmanim = new Zmanim(location, date);
  const sunset = zmanim.sunset();
  const sunsetTime = parseTime(sunset);
  if (isNaN(sunsetTime)) {
    throw new Error('Could not determine sunset');
  }
  if (time < sunsetTime) {
    return today;
  } else {
    return tomorrow;
  }
}


/* End Hebrew event/date functions */

const locations = {
  "Monroe": {
    lat: 41.3307,
    lng: -74.1865,
    il: false,
    tz: "America/New_York",
    name: "Monroe",
    country: "US"
  },
  "Lakewood": {
    lat: 40.0960,
    lng: -74.2092,
    il: false,
    tz: "America/New_York",
    name: "Lakewood",
    country: "US"
  },
  "Monsey": {
    lat: 41.1145,
    lng: -74.0660,
    il: false,
    tz: "America/New_York",
    name: "Monsey",
    country: "US"
  },
  "Brooklyn": {
    lat: 40.6782,
    lng: -73.9442,
    il: false,
    tz: "America/New_York",
    name: "Brooklyn",
    country: "US"
  },
  "Miami": {
    lat: 25.7617,
    lng: -80.1918,
    il: false,
    tz: "America/New_York",
    name: "Miami",
    country: "US"
  },
  "Los Angeles": {
    lat: 34.0522,
    lng: -118.2437,
    il: false,
    tz: "America/Los_Angeles",
    name: "Los Angeles",
    country: "US"
  }
}

const buildFormData = (formData, data, parentKey) => {
  if (data instanceof File) {
    formData.append(parentKey, data);
  } else if (Array.isArray(data) && data[0] instanceof File) {
    // Append each file with the same field name
    data.forEach(file => formData.append(parentKey, file));
  } else if (Array.isArray(data) && data.length === 0) {
    formData.append(`${parentKey}[]`, '');
  } else if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof DateTime)) {
    Object.keys(data).forEach(key => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? '' : data;
    formData.append(parentKey, value);
  }
}

const convertToFormData = (values) => {
  if (!values.media) {
    return values;
  }
  const formData = new FormData();
  buildFormData(formData, values);
  return formData;
};

// The GSM-7 basic + extension set — the only chars carriers bill at the cheap rate.
const GSM7_CHARS = new Set([
  ..."@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?",
  ..."¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
  ..."^{}\\[~]|€",
]);

// Common pasted (Word/Notes/iMessage) chars that look fine but aren't GSM-7.
// Mapped to their GSM-7 equivalent before stripping, so they survive as the
// intended character instead of disappearing.
const SMART_REPLACEMENTS = {
  '\u2018': "'", '\u2019': "'",   // ‘ ’  curly single quotes
  '\u201C': '"', '\u201D': '"',   // “ ”  curly double quotes
  '\u2013': '-', '\u2014': '-',   // – —  en / em dash
  '\u2026': '...',                // …    ellipsis
  '\u00A0': ' ',                  //      non-breaking space
  '\u2022': '*',                  // •    bullet
};

// Full sanitize: soft-replace the accidental smart punctuation, then drop
// anything still outside GSM-7 (emoji, symbols, etc.).
const sanitizeToGsm7 = (str = "") => {
  let out = "";
  for (const ch of str) {
    if (SMART_REPLACEMENTS[ch] !== undefined) {
      out += SMART_REPLACEMENTS[ch];   // replacement is always GSM-7 safe
    } else if (GSM7_CHARS.has(ch)) {
      out += ch;    // already valid, keep it
    }
    // else: un-mappable (emoji, stray symbol) — drop it
  }
  return out;
};

const downloadFile = async ({ url, method="GET", contentType = "text/csv; charset=utf-8" }) => {

  const data = await axios({
    method: method,
    url: url,
    responseType: 'blob',
    timeout: 60000,
    headers: {
      "Content-Type": contentType,
      "Content-disposition": "attachment;"
    }
  })

  console.log(data, "date^^^^^^^^^^^^^^^^^^")

  let filename = data.headers["content-disposition"].split("filename=")[1]

  const elem = window.document.createElement('a');
  elem.href = window.URL.createObjectURL(data.data);
  elem.download = filename;        
  document.body.appendChild(elem);
  elem.click();        
  document.body.removeChild(elem);
}


export {
  capitalize,
  regex,
  dateEnd,
  dateStart,
  formatTime,
  IsoDate,
  timeDiff,
  getLuxonDate,
  formatGenericDate,
  formatGenericTime,
  formatGenericDateTime,
  formatDayDate,
  formatDurationFromSeconds,
  addMinutes,
  relativeTime,
  formatCurrency,
  fxn,
  getCCFee,
  formatDisplay,
  formatPhoneNumber,
  formatLength,
  ivd,
  displayTruncated,
  getViewableRows,
  strToBool,
  equalIfNotNull,
  boolToStr,
  isValidEmail,
  detectEnter,
  getQueries,
  buildFormData,
  convertToFormData,

  getHebrewDate,
  getParsha,
  getEvents,
  getCandleLighting,
  getHavdala,
  getDayBefore,
  getHolidayEvents,

  checkBookingAvailabilityByDate,
  checkBookingAvailabilityByTime,

  sanitizeToGsm7,


  locations,


  downloadFile
}
